import Constants from '@/Constants';
import { AvailableLanguage } from '@/models/AvailableLanguage';
import Category from '@/models/Category';
import { EntityType } from '@/models/EntityTypes';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { articleTpl } from '@/services/CourseService';
import api from '@/services/HttpService';
import { uploadHtml } from '@/services/S3Service';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getItensClear, postItemClear, postItemRequest, putItemClear, putItemRequest } from '@/store/item/action';
import React, { useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import FamilySelectionBox from '../FamilySelectionBox';
import Modal from '../Modal';
import SelectCategoryForm from '../SelectCategoryForm';
import TagsInput from '../TagsInput';
import Upload from '../Upload';
import { formErrors, formInitialState, schema } from './constants';
import './style.scss';

interface NewArticleProps {
    handleOnCancel: () => void;
    language: LanguageType;
    item?: Item;
}

const NewItem = ({ item, handleOnCancel, language }: NewArticleProps) => {
    const [isEdit, setIsEdit] = useState(false);
    const [formState, setFormState] = useState(formInitialState);
    const { mainImage, name, author, category, category2, tags, editorState, workload, families } = formState;
    const [formBkpState, setFormBkpState] = useState(formInitialState);
    const [bkpReady, setBkpReady] = useState<boolean>(false);
    const [{ nameError, authorError, tagsError, editorStateError }, setFormError] = useState(formErrors);
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
    const [canPublish, setCanPublish] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);

    const history = useHistory();
    const dispatch = useDispatch();

    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const { success } = useSelector((state: ApplicationState) => state.itens);
    const {
        data: { categories: fetchedCategories },
    } = useSelector((state: ApplicationState) => state.categories);
    const [canRedirect, setCanRedirect] = useState<boolean>(false);

    useEffect(() => {
        resetNavigationToSetScreen(EntityType.CONTENT, item?.organizationId, identity.user.admin, history);

        return () => {
            setFormState(formInitialState);
            dispatch(getItensClear());
        };
    }, [item, identity?.user.admin]);

    useEffect(() => {
        if (canRedirect) {
            history.push('/contents/playlist/items');
        }
    }, [canRedirect]);

    useEffect(() => {
        let location: string | string[] = history.location.pathname;
        location = location.split('/');
        if (location.length === 4) setIsEdit(true);
    }, [history.location.pathname]);

    useEffect(() => {
        if (success && !item) {
            setFormState(formInitialState);
            dispatch(postItemClear());
            setCanRedirect(true);
        } else if (success && item) {
            dispatch(putItemClear());
            setCanRedirect(true);
        }
    }, [item, success]);

    const handleOnChange = useCallback(
        (fieldName: string, value: any) => {
            if (fieldName === 'editorState' && value === '<p><br></p>') {
                value = '';
            }

            setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
        },
        [fetchedCategories],
    );

    const getTranslatedArticlePath = (): string | undefined => {
        if (item && item.availableLanguages) {
            const contentLanguage: AvailableLanguage | undefined = item.availableLanguages.find((lang) => lang.locale === language);
            return (contentLanguage && contentLanguage.path) || item.path;
        }
    };

    useEffect(() => {
        if (item) {
            handleOnChange('author', item.author);
            handleOnChange('mainImage', item.mainImage);
            handleOnChange('name', item.name);
            handleOnChange('workload', item.workload);
            handleOnChange('category', item.categories && item.categories.length > 0 ? item.categories[0] : undefined);
            handleOnChange('category2', item.categories && item.categories.length > 1 ? item.categories[1] : undefined);
            handleOnChange('tags', item.tags && item.tags.length > 0 ? item.tags : []);
            handleOnChange('families', item.families && item.families.length > 0 ? item.families : []);

            getArticleContent();
        }

        async function getArticleContent() {
            const articlePath: string | undefined = getTranslatedArticlePath();
            if (articlePath) {
                const { data } = await api.get(articlePath);
                const contentHtml: HTMLElement = document.createElement('div');
                contentHtml.innerHTML = data;

                let articleContent: string = data;
                if (contentHtml.getElementsByClassName('container').length > 0) {
                    articleContent = contentHtml.getElementsByClassName('container')[0].innerHTML;
                }
                handleOnChange('editorState', articleContent);
                setBkpReady(true);
            } else {
                setBkpReady(true);
            }
        }
    }, [handleOnChange, item]);

    const handlePublish = async () => {
        await handleSubmit(true);
    };

    const handleSubmit = async (willPublish: boolean = false) => {
        setCanPublish(false);
        try {
            setFormError(formErrors);
            await schema.validate(
                {
                    name,
                    author,
                    editorState,
                },
                { abortEarly: false },
            );

            const path = `media/${new Date().getTime()}.html`;
            await uploadHtml(articleTpl.replace('{0}', editorState), path);

            const ITEM: Item = {
                '@class': '.MediaContent',
                prerequisites: true,
                mandatory: true,
                gradeWeight: 1,
                masteryScore: null,
                type: 'MediaContent',
                visibility: false,
                totalRating: 0,
                status: item ? item.status : 'DRAFT',
                identifier: `content_${new Date().getTime().toString()}`,
                applicationId: Constants.APP_ID,
                organizationId: families.length > 0 ? null : identity.user.orgId,
                mediaType: 'HTML',
                workload,
                locale: language,
                mainImage,
                path,
                name,
                author,
                tags,
                categories,
                moduleId: Constants.PLAYLIST_MODULE_ID,
                families,
            };

            if (item) {
                dispatch(
                    putItemRequest({
                        item: {
                            ...item,
                            name: ITEM.name,
                            path: ITEM.path,
                            type: ITEM.type,
                            author: ITEM.author,
                            workload: ITEM.workload,
                            categories: ITEM.categories,
                            tags: ITEM.tags,
                            locale: language,
                            mainImage,
                            families: ITEM.families,
                        },
                    }),
                );
            } else {
                dispatch(postItemRequest({ item: ITEM, willPublish }));
            }
        } catch (err) {
            if (err.inner) {
                err.inner.map((error: any) =>
                    setFormError((prevState) => ({
                        ...prevState,
                        [`${error.path}Error`]: true,
                    })),
                );
            } else {
                console.error(err);
            }
        }
    };

    useEffect(() => {
        const initialForm: boolean = _.isEqual(formBkpState, formInitialState);
        if (!initialForm && bkpReady) {
            setSaveDisabled(_.isEqual(formState, formBkpState));
        } else {
            setFormBkpState(formState);
        }

        setCanPublish(Boolean(mainImage && name && author && category?.id && tags.length > 0 && editorState && workload));
    }, [formState]);

    useEffect(() => {
        const addingAnotherLanguage: boolean = language !== (item && item.locale);
        if (bkpReady) {
            setFormBkpState(formInitialState);
            setBkpReady(false);
            if (addingAnotherLanguage) {
                setSaveDisabled(false);
            } else {
                setSaveDisabled(true);
            }
        }
    }, [language]);

    useEffect(() => {
        const updatedCategories: Category[] = [];

        if (category?.id) {
            updatedCategories.push(category);
        }

        if (category2?.id) {
            updatedCategories.push(category2);
        }

        setCategories(updatedCategories);
    }, [category, category2]);

    return (
        <>
            <Modal
                content={strings.LEAVE_SECTION_MESSAGE}
                handleLeftClick={handleOnCancel}
                icon="announcement"
                leftButtonText={strings.CONFIRM_EXIT}
                rightButtonText={strings.NO}
                title={strings.ATTENTION}
            />
            <div className="form-group new-item">
                <div className="ml-5 mr-5 mt-4">
                    <FamilySelectionBox disabled={!!item?.id} familyId={families[0]?.id} handleOnChange={handleOnChange} />
                </div>
                <div className="input-section">
                    <h1>
                        {strings.HEADER}
                        <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>
                    <div className="ml-5 mr-5">
                        <Upload
                            onComplete={(e) => handleOnChange('mainImage', e.key)}
                            onRemove={() => handleOnChange('mainImage', undefined)}
                            previewFile={{ uri: mainImage, name: mainImage }}
                            typeAccepted="IMAGE"
                        />
                    </div>
                </div>
                <div className="row input-section">
                    <div className="title-container">
                        <h1>
                            {strings.TITLE}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {nameError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <input
                        className={`form-control ${nameError ? 'error' : ''}`}
                        id="titleInput"
                        onChange={(e) => handleOnChange('name', e.target.value)}
                        placeholder={strings.TYPE_TITLE}
                        required
                        type="text"
                        value={name || ''}
                    />
                </div>
                <div className="row input-section">
                    <div className="title-container">
                        <h1>
                            {strings.AUTHOR}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {authorError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <input
                        className={`form-control ${authorError ? 'error' : ''}`}
                        id="titleInput"
                        onChange={(e) => handleOnChange('author', e.target.value)}
                        placeholder={strings.TYPE_AUTHOR}
                        required
                        type="text"
                        value={author || ''}
                    />
                </div>
                <div className="row input-section">
                    <div className="title-container">
                        <h1>
                            {strings.ESTIMATED_CONSUMPTION_TIME}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                    </div>
                    <input
                        className="form-control"
                        id="timeInput"
                        onChange={(e) => handleOnChange('workload', parseInt(e.target.value, 10))}
                        placeholder={strings.MIN_PLACEHOLDER}
                        required
                        type="number"
                        value={workload || ''}
                    />
                </div>

                <div className="ml-5 mr-5 mt-4 required">
                    <SelectCategoryForm
                        categoriesForm={categories}
                        familyId={families[0]?.id}
                        handleOnChange={(fieldName, categories) => {
                            const [category, category2] = categories || [];
                            handleOnChange('category', category);
                            handleOnChange('category2', category2);
                        }}
                        isColumnFormat
                    />
                </div>

                <div className="row input-section">
                    <div className="d-flex flex-fill flex-column">
                        <div className="title-container">
                            <h1>
                                {strings.TAGS}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {tagsError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <TagsInput hasError={tagsError} selectedTags={tags} setTags={handleOnChange} />
                    </div>
                </div>
                <div className="input-section">
                    <div className="title-container">
                        <h1>
                            {strings.CONTENT}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {editorStateError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <div className="editor">
                        <ReactQuill
                            defaultValue={editorState || ''}
                            formats={Constants.reactQuillAvailableFormats}
                            onChange={(val) => handleOnChange('editorState', val)}
                            placeholder={strings.TYPE_TEXT}
                            style={{
                                width: '100%',
                                background: '#fff',
                                border: `${editorStateError ? '1px solid #f10' : ''}`,
                                color: '#808080',
                                fontFamily: '"Exo 2", sans-serif',
                                fontSize: '18px',
                            }}
                            theme="snow"
                            value={editorState || ''}
                        />
                    </div>
                </div>
                <div className="warning-input">
                    <p className="warning-text">
                        {Constants.symbol.ASTERISK}
                        {strings.REQUIRED_FIELDS}
                    </p>
                </div>
                <div className="d-flex vertical-center justify-content-end h-100 w-100 submit-buttons">
                    <button className="btn btn-link" data-target="#alert-modal" data-toggle="modal" type="button">
                        {strings.CANCEL}
                    </button>
                    {!isEdit && (
                        <>
                            <button className="btn btn-primary btn-sm save-close" disabled={!canPublish} onClick={() => handleSubmit()} type="button">
                                {strings.SAVE_AND_CLOSE}
                            </button>
                            {(!item || (item && item.status !== 'PUBLISHED')) && (
                                <button className="btn btn-primary btn-sm" disabled={!canPublish} onClick={handlePublish} type="button">
                                    {strings.PUBLISH}
                                </button>
                            )}
                        </>
                    )}
                    {isEdit && (
                        <>
                            <button
                                className="btn btn-primary btn-sm save-close"
                                disabled={saveDisabled || !canPublish}
                                onClick={() => handleSubmit()}
                                type="button"
                            >
                                {strings.SAVE_AND_CLOSE}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default NewItem;
